import { ReceiptText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { AppraisalSection } from '@/components/deals/appraisal/AppraisalSection'
import {
  TAX_JURISDICTIONS,
  transactionTaxName,
} from '@/components/deals/appraisal/appraisal-transaction-tax'
import { cn } from '@/lib/utils'

const PROPERTY_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'non_residential', label: 'Non-residential / mixed-use' },
]

export function TransactionTaxPanel({ postcodeArea, state, onStateChange }) {
  const taxName = transactionTaxName(state.jurisdiction)

  function updateState(patch) {
    onStateChange?.({ ...state, ...patch })
  }

  return (
    <AppraisalSection title="Purchase Tax" icon={ReceiptText}>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="transaction-tax-jurisdiction">Tax jurisdiction</Label>
          <Select
            value={state.jurisdiction}
            onValueChange={(jurisdiction) => updateState({ jurisdiction })}
          >
            <SelectTrigger id="transaction-tax-jurisdiction" className="w-full bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAX_JURISDICTIONS.map((jurisdiction) => (
                <SelectItem key={jurisdiction.value} value={jurisdiction.value}>
                  {jurisdiction.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Selected from {postcodeArea || 'the deal postcode'}. Change this if the property is in another UK jurisdiction.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Transaction type</Label>
          <div
            className="flex rounded-lg border border-border/50 bg-muted/30 p-1"
            role="group"
            aria-label="Transaction type"
          >
            {PROPERTY_TYPES.map((propertyType) => (
              <Button
                key={propertyType.value}
                type="button"
                variant="ghost"
                aria-pressed={state.propertyType === propertyType.value}
                onClick={() => updateState({ propertyType: propertyType.value })}
                className={cn(
                  'h-auto min-w-0 flex-1 whitespace-normal rounded-md border border-transparent px-2 py-2 text-[11px] font-bold leading-tight text-muted-foreground shadow-none hover:bg-background hover:text-foreground',
                  state.propertyType === propertyType.value && 'border-border/50 bg-background text-foreground shadow-sm hover:bg-background',
                )}
              >
                {propertyType.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {state.propertyType === 'residential' ? (
        <div className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-border bg-background p-4">
          <div>
            <Label htmlFor="additional-residential-property">
              Additional property / investment purchase
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Applies the jurisdiction-specific higher residential rates.
            </p>
          </div>
          <Switch
            id="additional-residential-property"
            checked={state.additionalResidentialProperty}
            onCheckedChange={(checked) => updateState({ additionalResidentialProperty: checked })}
            aria-label="Additional property or investment purchase"
          />
        </div>
      ) : null}

      <p className="mt-4 text-xs text-muted-foreground">
        {taxName} is an estimate based on the purchase price. Confirm the final tax treatment with a qualified adviser.
      </p>
    </AppraisalSection>
  )
}
